import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import '@/assets/styles/main__style.css'
import '@/assets/styles/themes.css'
import { API } from './lib/api'
import PushNotificationManager from './utils/pushNotifications'
import VersionManager from './utils/versionManager'
import CacheManager from './utils/cacheManager'
import TokenManager from './utils/tokenManager'
import SessionBootstrap from './utils/sessionBootstrap'
import { createWebAuthnManager } from './utils/webauthn'
import fileUrlMixin from './mixins/fileUrlMixin'
import { runtimeConfig } from './config/runtime'
import { viewTracker } from './lib/viewTracker'
import { isStandaloneLaunch } from './utils/pwaMode'
import { Capacitor } from '@capacitor/core';

const useMocks = (process.env.VUE_APP_USE_MOCKS || 'false') === 'true'
const api = new API({
  primaryBaseURL: runtimeConfig.apiPrimaryBaseURL,
  fallbackBaseURL: runtimeConfig.apiFallbackBaseURL,
  pingPath: runtimeConfig.apiPingPath
});

// Bootstrap token from localStorage if present
let savedToken = localStorage.getItem('token')

// Try to restore token from emergency backup if not found
if (!savedToken) {
  try {
    const emergencyBackup = sessionStorage.getItem('__ewert_emergency_backup');
    if (emergencyBackup) {
      const backupData = JSON.parse(emergencyBackup);
      if (backupData.token) {
        savedToken = backupData.token;
        localStorage.setItem('token', savedToken);
        sessionStorage.removeItem('__ewert_emergency_backup');
        console.log('[MAIN] Token restored from emergency backup');
      }
    }
  } catch (e) {
    console.warn('[MAIN] Failed to restore token from emergency backup:', e);
  }
}

// Try to restore from session preserve
if (!savedToken) {
  try {
    const sessionPreserve = localStorage.getItem('__ewert_session_preserve');
    if (sessionPreserve) {
      const sessionData = JSON.parse(sessionPreserve);
      if (sessionData.token) {
        savedToken = sessionData.token;
        localStorage.setItem('token', savedToken);
        localStorage.removeItem('__ewert_session_preserve');
        console.log('[MAIN] Token restored from session preserve');
      }
    }
  } catch (e) {
    console.warn('[MAIN] Failed to restore token from session preserve:', e);
  }
}

if (savedToken) {
  api.setToken(savedToken)
  console.log('[MAIN] Token loaded and applied to API');
}
async function prepareMocks() {
  const { worker } = await import("@/mock/browser");
  return worker.start();
}
const app = createApp(App)
const pushManager = new PushNotificationManager(api)
const versionManager = new VersionManager(api)
const cacheManager = new CacheManager()
const tokenManager = new TokenManager()
const sessionBootstrap = new SessionBootstrap({ api, cache: cacheManager, store })
const webAuthnManager = createWebAuthnManager(api)
viewTracker.setApi(api)

// Link version manager and push manager
versionManager.setPushManager(pushManager)

app.config.globalProperties.$api = api
app.config.globalProperties.$api_url = api.baseURL
app.config.globalProperties.$push = pushManager
app.config.globalProperties.$version = versionManager
app.config.globalProperties.$cache = cacheManager
app.config.globalProperties.$token = tokenManager
app.config.globalProperties.$webauthn = webAuthnManager
app.config.globalProperties.$sessionBootstrap = sessionBootstrap
app.config.globalProperties.$runtime = runtimeConfig
app.config.globalProperties.$platform = Capacitor.getPlatform();
app.config.globalProperties.$viewTracker = viewTracker

// Add global mixin for file URL handling
app.mixin(fileUrlMixin)

app.use(store).use(router)

// Apply theme from settings
function applyTheme(theme) {
  if (theme === 'dark') {
    document.documentElement.classList.add('dark')
  } else if (theme === 'light') {
    document.documentElement.classList.remove('dark')
  } else {
    // Auto theme based on system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    if (prefersDark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    
    // Add listener for system theme changes when in auto mode
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (!localStorage.getItem('theme') || localStorage.getItem('theme') === 'auto') {
        if (e.matches) {
          document.documentElement.classList.add('dark')
        } else {
          document.documentElement.classList.remove('dark')
        }
      }
    })
  }
}

// Load and apply theme on app initialization
async function initializeTheme() {
  try {
    // If we have a saved token, try to get user settings
    if (savedToken) {
      // Try to get theme from user settings

      const response = await api.get('api/auth/settings')
      if (response.settings && response.settings.theme) {
        applyTheme(response.settings.theme)
        return
      }
    }
    
    // Check for theme in localStorage (saved from previous settings changes)
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme) {
      applyTheme(savedTheme)
      return
    }
    
    // If no token or failed to get settings, use system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    if (prefersDark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  } catch (error) {
    console.warn('Failed to load theme settings, using system preference:', error)
    // Fallback to system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    if (prefersDark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }
}

// Register Service Worker for PWA
async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.warn('[SW] Service Workers not supported');
    return false;
  }

  try {
    console.log('[SW] Registering service worker...');
    
    // First check if SW file is accessible
    const swPaths = ['/sw.js', './sw.js'];
    let workingPath = null;
    
    for (const path of swPaths) {
      try {
        console.log(`[SW] Checking if ${path} is accessible...`);
        const response = await fetch(path);
        if (response.ok) {
          console.log(`[SW] ✅ ${path} is accessible (${response.status})`);
          workingPath = path;
          break;
        } else {
          console.warn(`[SW] ❌ ${path} returned ${response.status}`);
        }
      } catch (error) {
        console.warn(`[SW] ❌ ${path} fetch failed:`, error.message);
      }
    }
    
    if (!workingPath) {
      throw new Error('Service worker file not found at any expected path');
    }
    
    // Register the working path
    console.log(`[SW] Registering service worker at: ${workingPath}`);
    const registration = await navigator.serviceWorker.register(workingPath, {
      scope: '/'
    });
    
    console.log('[SW] ✅ Service worker registered successfully:', registration);
    
    // Wait for service worker to be ready
    await navigator.serviceWorker.ready;
    console.log('[SW] ✅ Service worker is ready');
    
    return registration;
  } catch (error) {
    console.error('[SW] ❌ Service worker registration failed:', error);
    
    // Show user-friendly error
    if (error.message.includes('not found')) {
      console.error('[SW] Service worker file is missing. Make sure sw.js exists in public folder.');
    }
    
    return false;
  }
}

// Register on load
if ('serviceWorker' in navigator) {
  window.addEventListener('load', registerServiceWorker);
}

// Initialize push notifications after app is mounted
async function initializeApp() {
  if (useMocks) {
    await prepareMocks()
  }

  await api.initializeBaseURL()
  app.config.globalProperties.$api_url = api.baseURL

  app.config.globalProperties.$isPWA = () => {
    return isStandaloneLaunch()
  }
  app.mount('#app')
  
  // Initialize theme
  await initializeTheme()
  
  // Initialize push notifications if user is logged in
  if (savedToken) {
    try {
      await sessionBootstrap.preload()
      await pushManager.initialize()
    } catch (error) {
      console.error('Failed to initialize push notifications:', error)
    }
  }
  
  // Clean expired cache entries on app start
  cacheManager.cleanup()
  
  // Create emergency token backup
  if (savedToken) {
    tokenManager.createEmergencyBackup()
  }
  
  // Set up token recovery event listener
  window.addEventListener('tokenRecovered', () => {
    console.log('Token recovery detected, reinitializing services...')
    // Reinitialize services that depend on token
    if (pushManager) {
      pushManager.syncCurrentSubscriptionWithServer().catch(error => {
        console.error('Failed to reinitialize push notifications after token recovery:', error)
      })
    }
  })
  
  // Start token integrity monitoring
  if (savedToken) {
    try {
      tokenManager.startMonitoring()
      console.log('Token integrity monitoring started')
    } catch (error) {
      console.error('Failed to start token monitoring:', error)
    }
  }
  
  // Set up message handler for Service Worker token requests
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'GET_TOKEN') {
        const token = localStorage.getItem('token');
        event.source.postMessage({
          type: 'TOKEN_RESPONSE',
          token: token
        });
      }
    });
  }
  
  // Initialize version manager
  try {
    // Restore session after potential update
    versionManager.restoreSession()
    
    // Initialize version checking
    await versionManager.initialize()
    
    // Set up global version check function
    window.checkForUpdates = () => versionManager.forceCheck()
  } catch (error) {
    console.error('Failed to initialize version manager:', error)
  }
}

initializeApp()
